import { useState, useMemo, useCallback } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Tab,
  Tabs,
  Chip,
  Skeleton,
  Button,
} from '@mui/material'
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium'
import { Check } from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from 'src/store/hooks'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import {
  selectAssignmentUserProgressV4,
  selectAssignmentUserProgressV4Status,
  fetchAllAssignmentUserProgressV4,
  fetchAssignmentDetailV4,
  fetchAssignmentSummariesV4,
  fetchAssignmentCredits,
  type AssignmentDetailV3,
  type AssignmentCreditsResponse,
  type UserAssignmentProgress,
} from '../../manage.slice'
import { transformToProgressRows } from '../utils/progress-transformers'
import { dataGridSx } from '../../styles'
import { giveCourseCredit } from '../../utils/api'
import { useNotifications } from '../../hooks/use-notifications'
import BulkCourseCreditModal from './modals/bulk-course-credit-modal'
import {
  CREDIT_SOURCE_CONFIG,
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../../constants'

interface ProgressCardTabbedProps {
  assignment: AssignmentDetailV3 | null
  isLoading: boolean
  credits: AssignmentCreditsResponse | null
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  let { children, value, index } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`progress-tabpanel-${index}`}
      aria-labelledby={`progress-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `progress-tab-${index}`,
    'aria-controls': `progress-tabpanel-${index}`,
  }
}

function sanitizeFilename(name: string | undefined): string {
  if (!name) return 'assignment-progress'
  return name
    .replace(/[^a-zA-Z0-9-_ ]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .toLowerCase()
}

export default function ProgressCardTabbed({
  assignment,
  isLoading,
  credits,
}: ProgressCardTabbedProps) {
  let dispatch = useAppDispatch()
  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let [activeTab, setActiveTab] = useState(0)
  let [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  let [selectedRowIDs, setSelectedRowIDs] = useState<string[]>([])
  let [bulkCreditModalOpen, setBulkCreditModalOpen] = useState(false)
  let [singleCourseGroupForModal, setSingleCourseGroupForModal] = useState<Map<
    string,
    {
      courseID: string
      courseName: string
      userIDs: string[]
      userNames: string[]
    }
  > | null>(null)

  let coursesApiRef = useGridApiRef()
  let usersApiRef = useGridApiRef()

  let userProgress = useAppSelector(
    selectAssignmentUserProgressV4,
  ) as UserAssignmentProgress[]
  let userProgressStatus = useAppSelector(selectAssignmentUserProgressV4Status)

  let progressRows = useMemo(
    () => transformToProgressRows(userProgress, assignment),
    [userProgress, assignment],
  )

  let validSelections = useMemo(
    () =>
      selectedRowIDs
        .map((id) => progressRows.find((row) => row.id === id))
        .filter((row) => row?.userID && row.status !== 'Completed'),
    [selectedRowIDs, progressRows],
  )

  let courseGroupsForPreview = useMemo(
    () =>
      validSelections
        .filter((row) => !!row)
        .reduce((groups, row) => {
          let existingGroup = groups.get(row.courseID)

          if (existingGroup) {
            return new Map(groups).set(row.courseID, {
              ...existingGroup,
              userIDs: [...existingGroup.userIDs, row.userID],
              userNames: [...existingGroup.userNames, row.userName],
            })
          }

          return new Map(groups).set(row.courseID, {
            courseID: row.courseID,
            courseName: row.courseName,
            userIDs: [row.userID],
            userNames: [row.userName],
          })
        }, new Map<string, { courseID: string; courseName: string; userIDs: string[]; userNames: string[] }>()),
    [validSelections],
  )

  let uniqueUserCount = useMemo(() => {
    let allUserIDs = Array.from(courseGroupsForPreview.values()).flatMap(
      (group) => group.userIDs,
    )
    return new Set(allUserIDs).size
  }, [courseGroupsForPreview])

  let courseCreditsByKey = useMemo(
    () =>
      new Map(
        (credits?.assignmentCourseCredits ?? []).map((c) => [
          `${c.userID}-${c.courseID}`,
          c.source,
        ]),
      ),
    [credits],
  )

  let assignmentCreditsByUser = useMemo(
    () =>
      new Map(
        (credits?.assignmentCredits ?? []).map((c) => [c.userID, c.source]),
      ),
    [credits],
  )

  let handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue)
      setSelectedRowIDs([])
    },
    [],
  )

  let handleRowSelectionChange = useCallback(
    (newSelection: any) => {
      // Handle 'exclude' type (select all optimization)
      let selectedIDs =
        newSelection?.type === 'exclude'
          ? (() => {
              let excludedIDs = Array.from(newSelection.ids || []) as string[]
              // Select all data row IDs except the excluded ones
              return progressRows
                .map((row) => row.id)
                .filter((id) => !excludedIDs.includes(id))
            })()
          : // Handle 'include' type (normal selection)
            newSelection?.type === 'include' && newSelection?.ids
            ? (Array.from(newSelection.ids) as string[])
            : // Handle direct array
              Array.isArray(newSelection)
              ? newSelection
              : // Handle direct ids property
                newSelection?.ids
                ? (Array.from(newSelection.ids) as string[])
                : []

      setSelectedRowIDs(selectedIDs)
    },
    [progressRows],
  )

  let resetDataGridSelections = useCallback(() => {
    coursesApiRef.current?.setRowSelectionModel({
      type: 'include',
      ids: new Set(),
    })
    usersApiRef.current?.setRowSelectionModel({
      type: 'include',
      ids: new Set(),
    })
    setSelectedRowIDs([])
  }, [coursesApiRef, usersApiRef])

  let handleOpenCreditModal = useCallback(
    (
      userId: string,
      userName: string,
      courses: { courseID: string; courseName: string }[],
    ) => {
      let courseGroups = new Map<
        string,
        {
          courseID: string
          courseName: string
          userIDs: string[]
          userNames: string[]
        }
      >()

      courses.forEach((course) => {
        courseGroups.set(course.courseID, {
          courseID: course.courseID,
          courseName: course.courseName,
          userIDs: [userId],
          userNames: [userName],
        })
      })

      setSingleCourseGroupForModal(courseGroups)
      setBulkCreditModalOpen(true)
    },
    [],
  )

  let handleGiveBulkCourseCredit = async () => {
    if (!assignment?.id) return

    // Use singleCourseGroupForModal if set (individual button click), otherwise use courseGroupsForPreview (bulk selection)
    let activeGroups = singleCourseGroupForModal || courseGroupsForPreview

    if (activeGroups.size === 0) return

    setIsSubmitting(true)

    // Calculate unique user count for this operation
    let userCount = new Set(
      Array.from(activeGroups.values()).flatMap((group) => group.userIDs),
    ).size

    // Create payloads grouped by course
    let payloads = Array.from(activeGroups.values()).map((group) => ({
      assignmentID: assignment.id!,
      userIDs: group.userIDs,
      courseIDs: [group.courseID],
    }))

    // Execute API calls sequentially
    let results = await payloads.reduce(
      async (previousPromise, payload) => {
        let acc = await previousPromise
        let group = Array.from(activeGroups.values()).find(
          (g) => g.courseID === payload.courseIDs[0],
        )
        let courseName = group?.courseName || 'Unknown'

        try {
          await giveCourseCredit(payload)
          return {
            ...acc,
            successful: [...acc.successful, courseName],
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Failed to give credit for ${courseName}:`, error)
          return {
            ...acc,
            failed: [...acc.failed, { courseName, error }],
          }
        }
      },
      Promise.resolve({
        successful: [] as string[],
        failed: [] as { courseName: string; error: any }[],
      }),
    )

    dispatch(fetchAssignmentDetailV4(assignment.id!))
    dispatch(
      fetchAllAssignmentUserProgressV4({
        assignmentId: assignment.id!,
        includeDetails: true,
      }),
    )
    dispatch(fetchAssignmentCredits(assignment.id!))
    dispatch(fetchAssignmentSummariesV4())

    if (results.failed.length === 0) {
      showSuccess(
        `Successfully gave credit to ${userCount} user(s) across ${payloads.length} course(s).`,
      )
    } else if (results.successful.length === 0) {
      showError(`Failed to give credit for all courses.`)
    } else {
      showError(
        `Gave credit for ${results.successful.length} course(s), but failed for: ${results.failed.map((f) => f.courseName).join(', ')}`,
      )
    }

    resetDataGridSelections()
    setBulkCreditModalOpen(false)
    setSingleCourseGroupForModal(null)
    setSelectedRowIDs([])
    setIsSubmitting(false)
  }

  let courseColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'courseName',
        headerName: 'Course Name',
        width: 300,
      },
      {
        field: 'userName',
        headerName: 'User (Email)',
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.rowNode.type === 'group') return null
          return params.value ? (
            <Chip
              label={params.value}
              size="small"
              color={params.value === 'Completed' ? 'success' : 'default'}
            />
          ) : null
        },
      },
      {
        field: 'completionDate',
        headerName: 'Date Completed',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.rowNode.type === 'group') return null
          return (
            <Box className="flex items-center justify-center w-full h-full">
              <Typography variant="body2">{params.value}</Typography>
            </Box>
          )
        },
      },
      {
        field: 'creditSource',
        headerName: 'Credit Source',
        width: 160,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.rowNode.type === 'group') return null
          let source = courseCreditsByKey.get(
            `${params.row.userID}-${params.row.courseID}`,
          )
          if (!source) return null
          let config = CREDIT_SOURCE_CONFIG[source]
          return (
            <Chip
              label={config.label}
              color={config.color}
              size="small"
              variant="outlined"
            />
          )
        },
        sortable: false,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 190,
        sortable: false,
        disableExport: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams) => {
          if (params.rowNode.type === 'group') return null

          let isCompleted = params.row.status === 'Completed'

          return (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Check />}
              disabled={isSubmitting || isCompleted}
              onClick={() => {
                handleOpenCreditModal(params.row.userID, params.row.userName, [
                  {
                    courseID: params.row.courseID,
                    courseName: params.row.courseName,
                  },
                ])
              }}
              aria-label={`Give credit for ${params.row.courseName} to ${params.row.userName}`}
            >
              Give Course Credit
            </Button>
          )
        },
      },
    ],
    [isSubmitting, handleOpenCreditModal, courseCreditsByKey],
  )

  let userColumns: GridColDef[] = useMemo(
    () => [
      // Assignment-level columns (parent rows only)
      {
        field: 'userName',
        headerName: 'User (Email)',
        width: 300,
        renderCell: (params) => {
          if (params.rowNode.type === 'group') {
            return (
              <Box className="flex items-center w-full h-full">
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {params.value}
                </Typography>
              </Box>
            )
          }
          return null
        },
      },
      // Course-level columns (child rows only)
      {
        field: 'courseName',
        headerName: 'Course Name',
        flex: 1,
        valueGetter: (value, row) => (row.userID ? value || '' : ''),
        renderCell: (params) => {
          if (params.rowNode.type !== 'group' && params.value) {
            return (
              <Box className="flex items-center w-full h-full">
                <Typography variant="body2">{params.value}</Typography>
              </Box>
            )
          }
          return null
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => (row.userID ? value : ''),
        renderCell: (params) => {
          if (params.rowNode.type !== 'group' && params.value) {
            return (
              <Box className="flex items-center justify-center w-full h-full">
                <Chip
                  label={params.value}
                  size="small"
                  color={
                    params.row.status === 'Completed' ? 'success' : 'default'
                  }
                />
              </Box>
            )
          }
          return null
        },
      },
      {
        field: 'completionDate',
        headerName: 'Date Completed',
        width: 180,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.rowNode.type !== 'group' && params.value) {
            return (
              <Box className="flex items-center justify-center w-full h-full">
                <Typography variant="body2">{params.value}</Typography>
              </Box>
            )
          }
          return null
        },
      },
      {
        field: 'creditSource',
        headerName: 'Credit Source',
        width: 160,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.rowNode.type === 'group') {
            let childrenIds = params.rowNode.children || []
            let firstChildRow = progressRows.find(
              (row) => row.id === childrenIds[0],
            )
            let source = firstChildRow
              ? assignmentCreditsByUser.get(firstChildRow.userID)
              : undefined
            if (!source) return null
            let config = CREDIT_SOURCE_CONFIG[source]
            return (
              <Chip
                label={config.label}
                color={config.color}
                size="small"
                variant="outlined"
              />
            )
          }
          let source = courseCreditsByKey.get(
            `${params.row.userID}-${params.row.courseID}`,
          )
          if (!source) return null
          let config = CREDIT_SOURCE_CONFIG[source]
          return (
            <Chip
              label={config.label}
              color={config.color}
              size="small"
              variant="outlined"
            />
          )
        },
        sortable: false,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 200,
        sortable: false,
        disableExport: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams) => {
          // Parent row (user): Show "Give Assignment Credit" button
          if (params.rowNode.type === 'group') {
            // Check if all courses are completed for this user
            let childrenIds = params.rowNode.children || []
            let allCompleted = childrenIds.every((childId) => {
              let childRow = progressRows.find((row) => row.id === childId)
              return childRow && childRow.status === 'Completed'
            })

            // Get user info from first child row
            let firstChildId = childrenIds[0]
            let firstChildRow = progressRows.find(
              (row) => row.id === firstChildId,
            )
            let userId = firstChildRow?.userID
            let userName = firstChildRow?.userName

            return (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Check />}
                disabled={isSubmitting || allCompleted || !userId}
                onClick={() => {
                  if (!userId || !userName || !assignment) return

                  // Filter out completed courses
                  let incompleteCourses = assignment.courses
                    .filter((course) => {
                      // Find the progress row for this user+course combination
                      let progressRow = childrenIds
                        .map((childId) =>
                          progressRows.find((row) => row.id === childId),
                        )
                        .find((row) => row?.courseID === course.id)

                      // Only include if not completed
                      return progressRow && progressRow.status !== 'Completed'
                    })
                    .map((course) => ({
                      courseID: course.id,
                      courseName: course.displayName || course.name,
                    }))

                  handleOpenCreditModal(userId, userName, incompleteCourses)
                }}
                aria-label={`Give assignment credit to ${userName || 'user'}`}
              >
                Give Assignment Credit
              </Button>
            )
          }

          // Child row (course): Show "Give Course Credit" button
          let isCompleted = params.row.status === 'Completed'

          return (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Check />}
              disabled={isSubmitting || isCompleted}
              onClick={() => {
                handleOpenCreditModal(params.row.userID, params.row.userName, [
                  {
                    courseID: params.row.courseID,
                    courseName: params.row.courseName,
                  },
                ])
              }}
              aria-label={`Give credit for ${params.row.courseName} to ${params.row.userName}`}
            >
              Give Course Credit
            </Button>
          )
        },
      },
    ],
    [
      isSubmitting,
      progressRows,
      handleOpenCreditModal,
      assignment,
      courseCreditsByKey,
      assignmentCreditsByUser,
    ],
  )

  let coursesInitialState = useKeepGroupedColumnsHidden({
    apiRef: coursesApiRef,
    initialState: {
      rowGrouping: { model: ['courseName'] },
      pagination: {
        paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
      },
    },
  })

  let usersInitialState = useKeepGroupedColumnsHidden({
    apiRef: usersApiRef,
    initialState: {
      rowGrouping: { model: ['userName'] },
      pagination: {
        paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
      },
    },
  })

  if (isLoading || userProgressStatus === 'pending') {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" className="mb-4">
            Grouped Analysis
          </Typography>
          <Typography variant="body2" className="mb-4">
            Loading progress data...
          </Typography>
          <Skeleton variant="rectangular" height={400} />
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <Typography variant="h6" className="px-6 pt-6 mb-2">
          Grouped Analysis
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          className="px-6 mb-4"
        >
          Pivot tables for running reports on completion statuses grouped by
          user or course
        </Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Progress view tabs"
        >
          <Tab label="By Course" {...a11yProps(0)} />
          <Tab label="By User" {...a11yProps(1)} />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {validSelections.length >= 2 && (
            <Box
              className="flex items-center justify-end gap-2 p-4"
              sx={{ backgroundColor: 'background.paperDark' }}
            >
              <Chip
                label={`${validSelections.length} row${validSelections.length !== 1 ? 's' : ''} selected`}
                variant="outlined"
                color="secondary"
                size="medium"
              />
              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<Check />}
                onClick={() => setBulkCreditModalOpen(true)}
                disabled={isSubmitting}
              >
                Give Bulk Credit
              </Button>
            </Box>
          )}

          <Box className="px-6 pb-6">
            <DataGridPremium
              sx={dataGridSx}
              columns={courseColumns}
              rows={progressRows}
              apiRef={coursesApiRef}
              initialState={coursesInitialState}
              pagination
              pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={handleRowSelectionChange}
              groupingColDef={{
                hideDescendantCount: true,
              }}
              showToolbar
              slots={{
                toolbar: () => (
                  <Box sx={{ backgroundColor: 'background.paperDark' }}>
                    <DataGridToolbar
                      fileName={`${sanitizeFilename(assignment?.name)}-by-course`}
                    />
                  </Box>
                ),
              }}
            />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {validSelections.length >= 2 && (
            <Box
              className="flex items-center justify-end gap-2 p-4"
              sx={{ backgroundColor: 'background.paperDark' }}
            >
              <Chip
                label={`${validSelections.length} row${validSelections.length !== 1 ? 's' : ''} selected`}
                variant="outlined"
                color="secondary"
                size="medium"
              />
              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<Check />}
                onClick={() => setBulkCreditModalOpen(true)}
                disabled={isSubmitting}
              >
                Give Bulk Credit
              </Button>
            </Box>
          )}

          <Box className="px-6 pb-6">
            <DataGridPremium
              sx={dataGridSx}
              rows={progressRows}
              columns={userColumns}
              apiRef={usersApiRef}
              initialState={usersInitialState}
              pagination
              pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={handleRowSelectionChange}
              groupingColDef={{
                hideDescendantCount: true,
              }}
              showToolbar
              slots={{
                toolbar: () => (
                  <Box sx={{ backgroundColor: 'background.paperDark' }}>
                    <DataGridToolbar
                      fileName={`${sanitizeFilename(assignment?.name)}-by-user`}
                    />
                  </Box>
                ),
              }}
            />
          </Box>
        </TabPanel>
      </Card>

      <BulkCourseCreditModal
        isOpen={bulkCreditModalOpen}
        courseGroups={singleCourseGroupForModal || courseGroupsForPreview}
        totalUsers={
          singleCourseGroupForModal
            ? (() => {
                let allUserIDs = Array.from(
                  singleCourseGroupForModal.values(),
                ).flatMap((group) => group.userIDs)
                return new Set(allUserIDs).size
              })()
            : uniqueUserCount
        }
        isSubmitting={isSubmitting}
        onClose={() => {
          setBulkCreditModalOpen(false)
          setSingleCourseGroupForModal(null)
        }}
        onConfirm={handleGiveBulkCourseCredit}
      />

      {NotificationComponent}
    </>
  )
}
